// Enregistrement automatique (≈ 800 ms après la dernière frappe) du symptôme, du contexte et du titre.
// Réseau coupé : le brouillon reste sur l'appareil (localStorage, protégé par try/catch) et repart au retour du réseau.
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ErreurApi } from '../../lib/api.ts';
import type { EtatEnregistrement } from '../../components/ui.tsx';
import type { ContexteIntervention, Intervention } from './types.ts';

export interface Champs { title?: string; symptom?: string | null; context?: ContexteIntervention }

const cle = (id: string) => `trame.intervention.${id}.brouillon`;
function lireLocal(id: string): Champs | null {
  try { const v = localStorage.getItem(cle(id)); return v ? JSON.parse(v).champs : null; } catch { return null; }
}
function ecrireLocal(id: string, champs: Champs | null) {
  try {
    if (champs) localStorage.setItem(cle(id), JSON.stringify({ champs, quand: new Date().toISOString() }));
    else localStorage.removeItem(cle(id));
  } catch { /* stockage indisponible : l'enregistrement serveur reste la référence */ }
}
const heure = () => new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

export function useEnregistrementAuto(i: Intervention | undefined, enregistre: (r: Intervention) => void, signalerErreur: (m: string) => void) {
  const id = i?.id;
  const [valeurs, setValeurs] = useState<Required<Pick<Champs, 'title' | 'symptom' | 'context'>> | null>(null);
  const [etat, setEtat] = useState<EtatEnregistrement>('enregistre');
  const [quand, setQuand] = useState<string>();
  const enAttente = useRef<Champs | null>(null);
  const minuterie = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enCours = useRef(false);
  const rappel = useRef(enregistre); rappel.current = enregistre;
  const rappelErreur = useRef(signalerErreur); rappelErreur.current = signalerErreur;

  const envoyer = useCallback(async () => {
    if (!id || !enAttente.current || enCours.current) return;
    if (minuterie.current) { clearTimeout(minuterie.current); minuterie.current = null; }
    const champs = enAttente.current;
    enAttente.current = null;
    enCours.current = true;
    setEtat('en_cours');
    try {
      const r = await api.patch<Intervention>(`/api/interventions/${id}`, champs);
      rappel.current(r);
      const ensuite = enAttente.current as Champs | null;
      if (ensuite) { ecrireLocal(id, ensuite); setEtat('modifie'); }
      else { ecrireLocal(id, null); setEtat('enregistre'); setQuand(heure()); }
    } catch (e) {
      const ensuite = enAttente.current as Champs | null; // saisies arrivées pendant l'envoi
      enAttente.current = { ...champs, ...ensuite, context: { ...champs.context, ...ensuite?.context } };
      if (e instanceof ErreurApi && e.code === 'hors_connexion') {
        ecrireLocal(id, enAttente.current);
        setEtat('local');
      } else if (e instanceof ErreurApi && (e.status === 409 || e.status === 400 || e.status === 403 || e.status === 404)) {
        // Refus définitif (intervention clôturée, saisie invalide) : on n'insiste pas, on le dit.
        ecrireLocal(id, null);
        enAttente.current = null;
        setEtat('erreur');
        rappelErreur.current(e.message);
      } else {
        ecrireLocal(id, enAttente.current);
        setEtat('erreur');
        minuterie.current = setTimeout(() => { void envoyer(); }, 5000);
      }
    } finally {
      enCours.current = false;
      if (enAttente.current && !minuterie.current) minuterie.current = setTimeout(() => { void envoyer(); }, 800);
    }
  }, [id]);

  // Initialisation une seule fois par intervention (les saisies en cours ne sont jamais écrasées par une réponse serveur).
  useEffect(() => {
    if (!i) return;
    const base = { title: i.title, symptom: i.symptom ?? '', context: i.context ?? {} };
    const local = i.modifiable ? lireLocal(i.id) : null;
    if (local) {
      setValeurs({ ...base, ...local, context: { ...base.context, ...local.context } } as any);
      enAttente.current = local;
      setEtat('local');
      void envoyer();
    } else {
      if (!i.modifiable) ecrireLocal(i.id, null);
      setValeurs(base);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const reprise = () => { if (enAttente.current) void envoyer(); };
    window.addEventListener('online', reprise);
    return () => {
      window.removeEventListener('online', reprise);
      // Départ de la page avec une saisie non envoyée : elle reste sur l'appareil et part tout de suite.
      if (id && enAttente.current) { ecrireLocal(id, enAttente.current); void envoyer(); }
      if (minuterie.current) clearTimeout(minuterie.current);
    };
  }, [envoyer, id]);

  const modifier = useCallback((champs: Champs, immediat = false) => {
    if (!id) return;
    setValeurs((v) => (v ? { ...v, ...champs, context: champs.context ? { ...v.context, ...champs.context } : v.context } as any : v));
    const prec = enAttente.current;
    enAttente.current = { ...prec, ...champs, ...(champs.context || prec?.context ? { context: { ...prec?.context, ...champs.context } } : {}) };
    ecrireLocal(id, enAttente.current);
    setEtat('modifie');
    if (minuterie.current) clearTimeout(minuterie.current);
    minuterie.current = setTimeout(() => { minuterie.current = null; void envoyer(); }, immediat ? 0 : 800);
  }, [id, envoyer]);

  return { valeurs, modifier, etat, quand };
}
